import type {
    CreateObjectsBatchRequest,
    StationAccessType,
    StationObject,
} from '../../api/station/stationApiTypes';

/**
 * Which positions of an existing station are free, and what to send to fill them.
 *
 * ── The question this owns ─────────────────────────────────────────────────
 *
 * *"Which numbered positions of this station can take a new object?"* — one
 * definition, because the backend answers the same question independently when
 * it rejects a request (`createStationObjects` refuses a `door` above
 * `door_count` and one already held by a live object), and two answers that
 * drift mean the operator sees a 400 the form promised could not happen.
 *
 * The creation wizard has the same question in its degenerate form: a station
 * created seconds ago has nothing at any position, so `buildCreateObjectsRequest`
 * numbers its objects `1..N` directly. That shortcut is CORRECT and is left
 * alone — `stationPositions.test.ts` pins that `freePositions` on an empty
 * station returns exactly `1..N`, so the day the general answer changes, the
 * wizard's copy fails loudly instead of quietly disagreeing (ADR-0014's shape
 * for copies that cannot import each other — except here they could, and the
 * test says why they need not).
 *
 * ── Why the buffer is subtracted from the END ──────────────────────────────
 *
 * `buffer_doors_count` is the held-back/technical tail of the cabinet and is
 * subtracted from what customers can book
 * (`availabilityService.capacityReductionForDisplay`). `door_count` numbers the
 * whole cabinet, so the bookable positions are `1..(doorCount - buffer)` and the
 * buffer keeps the tail — the same convention `buildCreateObjectsRequest` uses.
 */

export interface StationPositionState {
    doorCount: number;
    bufferDoorsCount: number;
    objects: readonly Pick<StationObject, 'door'>[];
}

/** How many positions this station offers for bookable objects. */
export const bookablePositionCount = (state: {
    doorCount: number;
    bufferDoorsCount: number;
}): number => Math.max(0, (state.doorCount ?? 0) - (state.bufferDoorsCount ?? 0));

/** Positions an object already stands at. */
export const occupiedPositions = (
    objects: readonly Pick<StationObject, 'door'>[],
): number[] => Array.from(new Set(
    objects
        .map((object) => object.door)
        .filter((door): door is number => typeof door === 'number' && Number.isInteger(door)),
)).sort((a, b) => a - b);

/**
 * The free bookable positions, ascending.
 *
 * Deliberately excludes the buffer tail. An operator who needs those positions
 * back hands them over by lowering the buffer on the station form, which is a
 * capacity decision with its own guard — not something an "add objects" dialog
 * should do silently.
 */
export const freePositions = (state: StationPositionState): number[] => {
    const taken = new Set(occupiedPositions(state.objects));
    const positions: number[] = [];
    for (let door = 1; door <= bookablePositionCount(state); door += 1) {
        if (!taken.has(door)) positions.push(door);
    }
    return positions;
};

export interface AddPlan {
    /** The positions the new objects will occupy, ascending. */
    doors: number[];
    /** `door_count` the station must have. Equal to the current one when it fits. */
    newDoorCount: number;
    /** Positions that have to be added — zero when the station already fits. */
    addedPositions: number;
}

/**
 * Where `count` new objects would go, and how far the station must grow for it.
 *
 * ── Why this is not `count - free.length` ──────────────────────────────────
 *
 * That was the first version and it is wrong whenever the buffer is non-zero.
 * `door_count` numbers the whole cabinet and the buffer holds back its TAIL, so
 * growing `door_count` by N moves the bookable ceiling by N — it does not append
 * N positions past the old `door_count`. A station of 10 positions with 2 held
 * back and all 8 bookable ones filled, grown by 3, becomes 13 positions with a
 * bookable ceiling of 11: the free positions are **9, 10, 11**, not 11, 12, 13.
 * Writing the naive answer would have dropped objects into the held-back tail
 * while leaving two bookable positions empty — and the backend would have
 * accepted it, because it only checks `door <= door_count`.
 *
 * So the ceiling is raised one position at a time until enough positions below
 * it are free. One at a time rather than by arithmetic because a position can
 * be occupied ABOVE the current ceiling (an object standing in the buffer tail,
 * or left behind by a capacity change), and each such position absorbs one step
 * of the growth without yielding a free slot.
 */
export const planAddedObjects = (
    state: StationPositionState,
    count: number,
): AddPlan => {
    const current = bookablePositionCount(state);
    const buffer = Math.max(0, state.bufferDoorsCount ?? 0);
    const taken = new Set(occupiedPositions(state.objects));

    if (count <= 0) {
        return { doors: [], newDoorCount: state.doorCount, addedPositions: 0 };
    }

    // Walk the positions from 1, raising the ceiling for each one past it, and
    // collect the free ones. Terminates in at most `count + taken.size` steps:
    // `taken` is finite, so every position beyond its maximum is free.
    const doors: number[] = [];
    let ceiling = current;
    let door = 1;
    while (doors.length < count) {
        if (door > ceiling) ceiling = door;
        if (!taken.has(door)) doors.push(door);
        door += 1;
    }

    const newDoorCount = Math.max(state.doorCount, ceiling + buffer);
    return {
        doors,
        newDoorCount,
        addedPositions: newDoorCount - state.doorCount,
    };
};

export interface AddObjectsInput {
    stationId: string;
    productId: string;
    /** Positions to fill — normally the head of `freePositions`. */
    doors: readonly number[];
    /** `auto` names them `<prefix> <position>`; `manual` uses `names`. */
    naming: 'auto' | 'manual';
    namePrefix: string;
    names: readonly string[];
    doorMechanism: StationAccessType;
    /** Applied to every object in auto mode — they are alike by definition. */
    sharedVolumeCm3: number | null;
    sharedNote: string;
}

/**
 * The request that adds objects to a station that already exists.
 *
 * Two things differ from the wizard's builder and both matter:
 *
 * 1. **Positions come in, they are not derived from the index.** The second
 *    batch at a station does not start at 1.
 * 2. **The auto name carries the POSITION, not the index.** Naming the 11th
 *    compartment "Fach 1" because it is the first of its batch is how an
 *    operator ends up with two "Fach 1" in one cabinet and no way to tell the
 *    customer which door to open.
 */
// The BATCH shape, not the grouped one: the dialog adds one size at a time,
// so a single product covers the whole add.
export const buildAddObjectsRequest = (input: AddObjectsInput): CreateObjectsBatchRequest => {
    const prefix = input.namePrefix.trim();
    const note = input.sharedNote.trim();

    return {
        stationId: input.stationId,
        productId: input.productId.trim(),
        // Always a pool, for the same reason the wizard sends it: `exclusive`
        // is a second Product over the same objects, not a choice made while
        // creating them (#1796).
        mode: 'any_of',
        objects: input.doors.map((door, index) => {
            const manualName = input.names[index]?.trim();
            const name = input.naming === 'manual'
                ? (manualName || `${door}`)
                : `${prefix} ${door}`.trim();
            return {
                name,
                door,
                doorMechanism: input.doorMechanism,
                ...(input.sharedVolumeCm3 !== null ? { volumeCm3: input.sharedVolumeCm3 } : {}),
                ...(note ? { note } : {}),
            };
        }),
    };
};
